import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment.local';

type ParamValue = string | number | boolean;

export interface RequestOptions {
  readonly params?: Readonly<Record<string, ParamValue>>;
  readonly withCredentials?: boolean;
  readonly headers?: Readonly<Record<string, string>>;
}

@Injectable({ providedIn: 'root' })
export class ApiClientService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  get<T>(path: string, options?: RequestOptions): Observable<T> {
    return this.http.get<T>(this.url(path), this.buildOptions(options));
  }

  getBlob(path: string, options?: RequestOptions): Observable<Blob> {
    return this.http.get(this.url(path), {
      ...(this.buildOptions(options) ?? {}),
      responseType: 'blob' as const,
    });
  }

  post<T>(path: string, body: unknown, options?: RequestOptions): Observable<T> {
    return this.http.post<T>(this.url(path), body, this.buildOptions(options));
  }

  postMultipart<T>(
    path: string,
    form: FormData,
    options?: RequestOptions,
  ): Observable<T> {
    return this.http.post<T>(this.url(path), form, this.buildOptions(options));
  }

  put<T>(path: string, body: unknown, options?: RequestOptions): Observable<T> {
    return this.http.put<T>(this.url(path), body, this.buildOptions(options));
  }

  patch<T>(path: string, body: unknown, options?: RequestOptions): Observable<T> {
    return this.http.patch<T>(this.url(path), body, this.buildOptions(options));
  }

  delete<T>(path: string, options?: RequestOptions): Observable<T> {
    return this.http.delete<T>(this.url(path), this.buildOptions(options));
  }

  private url(path: string): string {
    return path.startsWith('/') ? `${this.base}${path}` : `${this.base}/${path}`;
  }

  private buildOptions(options?: RequestOptions) {
    if (!options) return undefined;
    const httpOptions: {
      params?: HttpParams;
      withCredentials?: boolean;
      headers?: HttpHeaders;
    } = {};
    if (options.params) {
      let params = new HttpParams();
      for (const [key, value] of Object.entries(options.params)) {
        params = params.set(key, String(value));
      }
      httpOptions.params = params;
    }
    if (options.withCredentials !== undefined) {
      httpOptions.withCredentials = options.withCredentials;
    }
    if (options.headers) {
      let headers = new HttpHeaders();
      for (const [key, value] of Object.entries(options.headers)) {
        headers = headers.set(key, value);
      }
      httpOptions.headers = headers;
    }
    return httpOptions;
  }
}
